import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserPlus, UserCheck, UserX, TrendingUp, Phone, Mail, Calendar,
  LogOut, RefreshCw, Search, Filter, MoreVertical, Edit2, Trash2, Eye
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_immobilier-nantes/artifacts/rfdvblj1_Logo%20Inter%27Med%20Immobilier.png";

const statusConfig = {
  nouveau: { label: "Nouveau", color: "bg-blue-100 text-blue-700", icon: UserPlus },
  contacte: { label: "Contacté", color: "bg-yellow-100 text-yellow-700", icon: Phone },
  qualifie: { label: "Qualifié", color: "bg-green-100 text-green-700", icon: UserCheck },
  converti: { label: "Converti", color: "bg-emerald-500 text-white", icon: TrendingUp },
  perdu: { label: "Perdu", color: "bg-red-100 text-red-700", icon: UserX },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedLead, setSelectedLead] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [editForm, setEditForm] = useState({ status: "", notes: "" });

  const adminUser = JSON.parse(localStorage.getItem("admin_user") || "{}");
  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    fetchData();
  }, [token, navigate]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, getAuthHeaders()),
        axios.get(`${API}/stats`, getAuthHeaders()),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        toast.error("Session expirée. Veuillez vous reconnecter.");
        handleLogout();
      } else {
        toast.error("Erreur lors du chargement des données");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const handleUpdateLead = async () => {
    if (!selectedLead) return;
    try {
      await axios.patch(`${API}/leads/${selectedLead.id}`, editForm, getAuthHeaders());
      toast.success("Lead mis à jour avec succès");
      setShowEditDialog(false);
      fetchData();
    } catch (error) {
      console.error("Update error:", error);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleDeleteLead = async (leadId) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer ce lead ?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, getAuthHeaders());
      toast.success("Lead supprimé");
      fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Erreur lors de la suppression");
    }
  };

  const openEditDialog = (lead) => {
    setSelectedLead(lead);
    setEditForm({ status: lead.status, notes: lead.notes || "" });
    setShowEditDialog(true);
  };

  const openDetailDialog = (lead) => {
    setSelectedLead(lead);
    setShowDetailDialog(true);
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch =
      lead.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.prenom.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.telephone.includes(searchTerm);
    const matchesStatus = statusFilter === "all" || lead.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "-";
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <img src={LOGO_URL} alt="Inter'med Immobilier" className="h-10" />
              <div className="hidden md:block">
                <h1 className="font-heading text-lg text-harbor">CRM Administration</h1>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground hidden sm:block">
                {adminUser.nom || adminUser.email}
              </span>
              <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-btn">
                <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} data-testid="logout-btn">
                <LogOut size={16} className="mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8" data-testid="stats-grid">
            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-harbor/10 rounded-md text-harbor">
                    <Users size={20} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-harbor" data-testid="stat-total">{stats.total_leads}</div>
                    <div className="text-xs text-muted-foreground">Total leads</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-md text-blue-700">
                    <UserPlus size={20} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-blue-700" data-testid="stat-nouveau">{stats.leads_nouveau}</div>
                    <div className="text-xs text-muted-foreground">Nouveaux</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-yellow-100 rounded-md text-yellow-700">
                    <Phone size={20} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-yellow-700" data-testid="stat-contacte">{stats.leads_contacte}</div>
                    <div className="text-xs text-muted-foreground">Contactés</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-md text-green-700">
                    <UserCheck size={20} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-green-700" data-testid="stat-qualifie">{stats.leads_qualifie}</div>
                    <div className="text-xs text-muted-foreground">Qualifiés</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-emerald-100 rounded-md text-emerald-700">
                    <TrendingUp size={20} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-emerald-700" data-testid="stat-converti">{stats.leads_converti}</div>
                    <div className="text-xs text-muted-foreground">Convertis</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="stat-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-md text-purple-700">
                    <Calendar size={20} />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-purple-700" data-testid="stat-recent">{stats.recent_leads}</div>
                    <div className="text-xs text-muted-foreground">7 derniers jours</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Rechercher un lead..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
              data-testid="search-input"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-full sm:w-48" data-testid="status-filter">
              <Filter size={16} className="mr-2" />
              <SelectValue placeholder="Filtrer par statut" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les statuts</SelectItem>
              {Object.entries(statusConfig).map(([key, config]) => (
                <SelectItem key={key} value={key}>{config.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Leads Table */}
        <Card data-testid="leads-table-card">
          <CardHeader className="pb-4">
            <CardTitle className="font-heading text-xl text-harbor">
              Liste des leads ({filteredLeads.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12 text-muted-foreground">
                Chargement des données...
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-leads">
                Aucun lead trouvé
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leads-table">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Contact</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground hidden md:table-cell">Téléphone</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground hidden lg:table-cell">Dispositif</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground hidden lg:table-cell">Montant</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Statut</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground hidden sm:table-cell">Date</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLeads.map((lead) => {
                      const status = statusConfig[lead.status] || statusConfig.nouveau;
                      return (
                        <tr key={lead.id} className="border-b border-gray-100 hover:bg-gray-50" data-testid={`lead-row-${lead.id}`}>
                          <td className="py-4 px-4">
                            <div>
                              <div className="font-medium text-harbor">
                                {lead.prenom} {lead.nom}
                              </div>
                              <div className="text-sm text-muted-foreground flex items-center gap-1">
                                <Mail size={12} />
                                {lead.email}
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-4 hidden md:table-cell">
                            <a href={`tel:${lead.telephone}`} className="text-harbor hover:underline flex items-center gap-1">
                              <Phone size={14} />
                              {lead.telephone}
                            </a>
                          </td>
                          <td className="py-4 px-4 hidden lg:table-cell">
                            <span className="capitalize">{lead.dispositif_interesse?.replace("_", " ") || "-"}</span>
                          </td>
                          <td className="py-4 px-4 hidden lg:table-cell">
                            {formatCurrency(lead.montant_investissement)}
                          </td>
                          <td className="py-4 px-4">
                            <Badge className={status.color}>
                              {status.label}
                            </Badge>
                          </td>
                          <td className="py-4 px-4 text-sm text-muted-foreground hidden sm:table-cell">
                            {formatDate(lead.created_at)}
                          </td>
                          <td className="py-4 px-4 text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" data-testid={`lead-actions-${lead.id}`}>
                                  <MoreVertical size={16} />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => openDetailDialog(lead)}>
                                  <Eye size={14} className="mr-2" />
                                  Voir détails
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEditDialog(lead)}>
                                  <Edit2 size={14} className="mr-2" />
                                  Modifier
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  onClick={() => handleDeleteLead(lead.id)}
                                  className="text-red-600"
                                >
                                  <Trash2 size={14} className="mr-2" />
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-harbor">
              Modifier le lead
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Contact</Label>
                <p className="text-sm text-muted-foreground">
                  {selectedLead.prenom} {selectedLead.nom} - {selectedLead.email}
                </p>
              </div>
              <div>
                <Label htmlFor="edit-status">Statut</Label>
                <Select value={editForm.status} onValueChange={(value) => setEditForm({ ...editForm, status: value })}>
                  <SelectTrigger data-testid="edit-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-notes">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Ajouter des notes..."
                  rows={4}
                  data-testid="edit-notes-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleUpdateLead} className="bg-harbor hover:bg-harbor/90" data-testid="save-edit-btn">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-lg" data-testid="detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-harbor">
              Détails du lead
            </DialogTitle>
          </DialogHeader>
          {selectedLead && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Nom</Label>
                  <p className="font-medium">{selectedLead.nom}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Prénom</Label>
                  <p className="font-medium">{selectedLead.prenom}</p>
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">Email</Label>
                <p className="font-medium">{selectedLead.email}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Téléphone</Label>
                <p className="font-medium">{selectedLead.telephone}</p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Dispositif</Label>
                  <p className="font-medium capitalize">{selectedLead.dispositif_interesse?.replace("_", " ") || "-"}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Montant</Label>
                  <p className="font-medium">{formatCurrency(selectedLead.montant_investissement)}</p>
                </div>
              </div>
              {selectedLead.reduction_estimee && (
                <div>
                  <Label className="text-muted-foreground">Réduction estimée</Label>
                  <p className="font-medium text-growth">{formatCurrency(selectedLead.reduction_estimee)}</p>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Statut</Label>
                <Badge className={statusConfig[selectedLead.status]?.color || statusConfig.nouveau.color}>
                  {statusConfig[selectedLead.status]?.label || "Nouveau"}
                </Badge>
              </div>
              {selectedLead.notes && (
                <div>
                  <Label className="text-muted-foreground">Notes</Label>
                  <p className="text-sm bg-gray-50 p-3 rounded-md">{selectedLead.notes}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4 text-sm text-muted-foreground">
                <div>
                  <Label className="text-muted-foreground">Créé le</Label>
                  <p>{formatDate(selectedLead.created_at)}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Modifié le</Label>
                  <p>{formatDate(selectedLead.updated_at)}</p>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailDialog(false)}>
              Fermer
            </Button>
            <Button onClick={() => { setShowDetailDialog(false); openEditDialog(selectedLead); }} className="bg-harbor hover:bg-harbor/90">
              Modifier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
